import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaCheck, FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useVerifyExternalChargeMutation } from "../../store/api/externalChargeSlice";
import "./ReceivePayment.scss";
import { useTranslation } from "react-i18next";

const ReceivePaymentComplete = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);

  const referenceId = queryParams.get("referenceId");
  const resultCode = queryParams.get("resultCode");
  const hash = queryParams.get("hash");
  const orderNumber = queryParams.get("orderNumber");
  const amount = queryParams.get("amount");
  const cardType = queryParams.get("cardType");

  const { user } = useSelector((state) => state.UserReducer);
  const role = user?.role === "super_admin" ? "admin" : user?.role;

  const [status, setStatus] = useState("processing"); // "processing" | "success" | "failed" | "verify_error"
  const [txnInfo, setTxnInfo] = useState(null);
  const [verifyErrorMessage, setVerifyErrorMessage] = useState(null);

  const [verifyCharge] = useVerifyExternalChargeMutation();

  const doVerify = async () => {
    const chargeId = localStorage.getItem("external_charge_id");
    const chargeNumber = localStorage.getItem("external_charge_number");

    if (!chargeId) {
      setStatus("failed");
      return;
    }

    setStatus("processing");

    try {
      const res = await verifyCharge({
        external_charge_id: Number(chargeId),
        reference_id: referenceId,
        result_code: resultCode,
        hash: hash || undefined,
      }).unwrap();

      // Backend gave us a definitive answer — safe to clear and stop retrying.
      localStorage.removeItem("external_charge_id");
      localStorage.removeItem("external_charge_number");

      if (res.data.payment_status === "completed") {
        setStatus("success");
        setTxnInfo({
          charge_number: chargeNumber || res.data.charge_number,
          amount_cents: res.data.amount_cents,
          currency: res.data.currency,
        });
        toast.success(t("payment_received_success"));
      } else {
        setStatus("failed");
      }
    } catch (err) {
      const message = err?.data?.message || t("payment_verification_failed");

      // A prior attempt already resolved this — its message tells us the
      // real outcome, so use that instead of looping on retry.
      const alreadyProcessedMatch = message.match(
        /already processed:\s*(\w+)/i,
      );
      if (alreadyProcessedMatch) {
        localStorage.removeItem("external_charge_id");
        localStorage.removeItem("external_charge_number");
        if (alreadyProcessedMatch[1].toLowerCase() === "completed") {
          setStatus("success");
          toast.success(t("payment_received_success"));
        } else {
          setStatus("failed");
        }
        return;
      }

      // The card may already have been charged by Omni — we just don't know
      // yet because this verify call itself errored (network/server issue).
      // Keep the IDs so the same verification can be safely retried instead
      // of pushing the user toward a brand new (possibly duplicate) charge.
      setVerifyErrorMessage(message);
      setStatus("verify_error");
    }
  };

  useEffect(() => {
    doVerify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="payment-complete">
      <div className="p-0">
        {status === "processing" && (
          <div className="payment-success">
            <div className="success-card">
              <div className="success-icon" style={{ background: "#f0fdf4" }}>
                <div className="spinner-border text-success" role="status" />
              </div>
              <h2>{t("verifyingPayment")}...</h2>
              <p>{"ConfirmTransaction"}</p>
            </div>
          </div>
        )}

        {status === "success" && (
          <div className="payment-success">
            <div className="success-card">
              <div className="success-icon">
                <FaCheck />
              </div>
              <h2>{t("paymentReceived")}</h2>
              <p>{t("chargeProcessed")}</p>

              <div className="order-details">
                {txnInfo?.charge_number && (
                  <div className="detail-row">
                    <span>{t("chargeNo")}</span>
                    <span>{txnInfo.charge_number}</span>
                  </div>
                )}
                {orderNumber && (
                  <div className="detail-row">
                    <span>{t("orderId")}</span>
                    <span>{orderNumber}</span>
                  </div>
                )}
                {(amount || txnInfo?.amount_cents) && (
                  <div className="detail-row">
                    <span>{t("amountCharged")}</span>
                    <span>
                      ${((amount || txnInfo?.amount_cents) / 100).toFixed(2)}{" "}
                      {txnInfo?.currency || ""}
                    </span>
                  </div>
                )}
                {cardType && (
                  <div className="detail-row">
                    <span>{t("paymentMethod")}</span>
                    <span>{cardType}</span>
                  </div>
                )}
              </div>

              <div className="d-flex gap-2 justify-content-center">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => navigate(`/receive-payment-history`)}
                >
                  {t("viewHistory")}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => navigate(`/receive-payment`)}
                >
                  {t("new")} {t("charge")}
                </button>
              </div>
            </div>
          </div>
        )}

        {status === "verify_error" && (
          <div className="payment-success">
            <div className="success-card">
              <div className="success-icon failed-icon">
                <FaTimes />
              </div>
              <h2>Couldn't Confirm Payment</h2>
              <p>
                The card may already have been charged, but we couldn't
                confirm it with our system yet. Please don't create a new
                charge — tap Retry below.
              </p>
              {verifyErrorMessage && (
                <p className="text-danger">{verifyErrorMessage}</p>
              )}

              <div className="d-flex gap-2 justify-content-center">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={doVerify}
                >
                  Retry
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => navigate(`/receive-payment-history`)}
                >
                  {t("viewHistory")}
                </button>
              </div>
            </div>
          </div>
        )}

        {status === "failed" && (
          <div className="payment-success">
            <div className="success-card">
              <div className="success-icon failed-icon">
                <FaTimes />
              </div>
              <h2>{t("paymentFailed")}</h2>
              <p>{t("paymentFailedDesc")}</p>
              {orderNumber && (
                <div className="order-details">
                  <div className="detail-row">
                    <span>{t("orderId")}</span>
                    <span>{orderNumber}</span>
                  </div>
                </div>
              )}
              <div className="d-flex gap-2 justify-content-center">
                <button
                  type="button"
                  className="btn btn-primary btn-failed"
                  onClick={() => navigate(`/receive-payment`)}
                >
                  {t("tryAgain")}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => navigate(`/dashboard`)}
                >
                  {t("dashboard")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReceivePaymentComplete;
